import { defineComponent, inject, onMounted, PropType, ref } from "vue";
import { gsap, Flip } from "gsap/all";

gsap.registerPlugin(Flip);

interface NavigationLink {
  href: string;
  title: string;
}

export default defineComponent({
  emits: ["toggleSidebar"],
  props: {
    primaryLinks: { type: Array as PropType<NavigationLink[]>, required: true },
    secondaryLinks: {
      type: Array as PropType<NavigationLink[]>,
      required: true,
    },
  },
  setup(props, { emit }) {
    const emitter = inject("emitter");

    const openNavTL = ref(gsap.timeline());
    const closeNavTL = ref(gsap.timeline());
    const navStatus = ref(false);
    const isMobile = ref(false);
    const openSideBarAfterMenuClosed = ref("");
    const removeScrollLockAfterClose = ref(false);

    const toggleSidebar = (e: Event): void => {
      e.preventDefault();

      const target = e!.currentTarget as HTMLElement;
      const { dataset } = target;

      if (navStatus.value) {
        //close fullscreen navigation if open

        if (dataset.sidebar) {
          openSideBarAfterMenuClosed.value = dataset.sidebar;
        }

        toggleFullscreenMenu();
      } else {
        emit("toggleSidebar", dataset.sidebar);
      }
    };

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore:next-line
    emitter.on("is-mobile-changed", (value) => {
      let recreateTimelines = false;

      if (isMobile.value !== value) {
        recreateTimelines = true;
      }

      isMobile.value = value;

      if (recreateTimelines) {
        createMenuTimelines();
      }
    });

    const positionLightBehindKoempel = (): void => {
      const light = document.querySelector(".fullscreen_navigation__light");

      if (isMobile.value === true) {
        light!.classList.add("fullscreen_navigation__light--mobile-position");

        return;
      } else {
        light!.classList.remove(
          "fullscreen_navigation__light--mobile-position"
        );
      }

      const navContainer = document.querySelector(
        ".fullscreen_navigation__container"
      );
      const illustration = document.querySelector(
        ".fullscreen_navigation__illustration"
      );

      gsap.set(navContainer, { yPercent: 0, autoAlpha: 1 });
      gsap.set(illustration, { yPercent: 0, autoAlpha: 1 });

      const illustrationBoundingRect = illustration!.getBoundingClientRect();

      gsap.set(light, {
        left:
          illustrationBoundingRect.left + illustrationBoundingRect.width * 0.42,
        top:
          illustrationBoundingRect.top + illustrationBoundingRect.height * 0.45,
        yPercent: -50,
      });
    };

    onMounted(() => {
      createMenuTimelines();
    });

    const createMenuTimelines = (): void => {
      const navContainer = document.querySelector(
        ".fullscreen_navigation__container"
      );
      const illustration = document.querySelector(
        ".fullscreen_navigation__illustration"
      );
      const light = document.querySelector(".fullscreen_navigation__light");

      const openTL = gsap
        .timeline()
        .pause()
        .add(() => {
          document.body.classList.add("lockScroll");
        })

        .set(light, { autoAlpha: 0, scale: 1 })
        .add(() => {
          document
            .querySelector(".fullscreen_navigation")!
            .classList.add("fullscreen_navigation--active");
        })
        .set(illustration, { autoAlpha: 0 })
        .to(
          ".navigation__menu_toggle__item",
          { yPercent: -100, clearProps: true },
          0
        )
        .add(() => {
          document
            .querySelector(".navigation__menu_toggle")!
            .classList.add("navigation__menu_toggle--switched");
        })
        .fromTo(
          navContainer,
          { yPercent: 100, autoAlpha: 1, transformOrigin: "bottom center" },
          { yPercent: 0, autoAlpha: 1, duration: 1.4, ease: "power4.inOut" },
          0
        )
        .fromTo(
          illustration,
          { yPercent: 100, autoAlpha: 0 },
          { yPercent: 0, autoAlpha: 1, duration: 1, ease: "power4.out" },
          "-=0.6"
        );

      if (!isMobile.value) {
        openTL.fromTo(
          light,
          { autoAlpha: 0, scale: 1 },
          { autoAlpha: 0.5, scale: 1.2, duration: 0.4 },
          "-=0.6"
        );
        openTL.add("start-light-scale");
      } else {
        openTL.add("start-light-scale", "-=1");
      }

      openTL.to(
        light,
        { autoAlpha: 1, scale: 55, duration: 1, ease: "power4.inOut" },
        "start-light-scale+=0.1"
      );

      if (isMobile.value) {
        openTL.fromTo(
          ".fullscreen_navigation__main_menu_label,.fullscreen_navigation__secondary_menu_label,.fullscreen_navigation__tertiary_item",
          {
            yPercent: 100,
          },
          { yPercent: 0, stagger: 0.05 },
          "start-light-scale+=0.3"
        );
      } else {
        openTL
          .fromTo(
            ".fullscreen_navigation__main_menu_label",
            {
              yPercent: 100,
            },
            { yPercent: 0, stagger: 0.05 },
            "start-light-scale+=0.3"
          )
          .fromTo(
            ".fullscreen_navigation__secondary_menu_label",
            {
              yPercent: 100,
            },
            { yPercent: 0, stagger: 0.05 },
            "start-light-scale+=0.4"
          )
          .fromTo(
            ".fullscreen_navigation__tertiary_item",
            {
              yPercent: 100,
            },
            { yPercent: 0, stagger: 0.05 },
            "start-light-scale+=0.5"
          );
      }

      openTL.add(() => {
        document
          .querySelectorAll(".navigation__side_menu_item")!
          .forEach((menuitem) => {
            menuitem!.classList.add("no-mix-blendmode");
          });
      }, "start-light-scale+=0.5");

      document
        .querySelectorAll(".navigation__side_menu_item")!
        .forEach((item, index) => {
          openTL.add(function () {
            item.classList.add("navigation__side_menu_item--active");
          }, "start-light-scale+=" + (0.6 + 0.05 * index));
        });

      openTL.add(function () {
        document
          .querySelector(".navigation--corners")!
          .classList.add("no-mix-blendmode");
      }, "start-light-scale+=0.5");

      openNavTL.value = openTL;

      const closeTL = gsap
        .timeline()
        .pause()
        .add(() => {
          document
            .querySelector(".fullscreen_navigation--active")!
            .classList.remove("fullscreen_navigation--active");
        })

        .to(
          ".navigation__menu_toggle__item",
          { yPercent: -100, clearProps: true },
          0
        )
        .add(() => {
          document
            .querySelector(".navigation__menu_toggle--switched")!
            .classList.remove("navigation__menu_toggle--switched");
        })

        .to(
          [
            ".fullscreen_navigation__main_menu_label",
            ".fullscreen_navigation__secondary_menu_label",
          ],
          { yPercent: -100 },
          0
        )
        .to(
          navContainer,
          { yPercent: -100, autoAlpha: 1, duration: 1.4, ease: "power4.inOut" },
          0
        )
        .to(
          light,
          {
            scale: 0,
          },
          0
        )
        .add(() => {
          document
            .querySelectorAll(".navigation__side_menu_item")!
            .forEach((menuitem) => {
              menuitem!.classList.remove("no-mix-blendmode");
            });
        }, 0)
        .add(() => {
          if (removeScrollLockAfterClose.value) {
            document.body.classList.remove("lockScroll");
            removeScrollLockAfterClose.value = false;
          }
        });

      document
        .querySelectorAll(".navigation__side_menu_item")!
        .forEach((item) => {
          closeTL.add(() => {
            item.classList.remove("navigation__side_menu_item--active");
          }, 0);
        });
      closeTL.add(() => {
        document
          .querySelector(".navigation--corners")!
          .classList.remove("no-mix-blendmode");

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore:next-line
        emitter.emit("fullscreen-menu-closed");

        if (openSideBarAfterMenuClosed.value !== "") {
          emit("toggleSidebar", openSideBarAfterMenuClosed.value);

          openSideBarAfterMenuClosed.value = "";
        }
      }, "-=0.5");

      closeNavTL.value = closeTL;
    };

    const toggleFullscreenMenu = (removeScrollLock = false): void => {
      removeScrollLockAfterClose.value = removeScrollLock;

      if (!navStatus.value) {
        closeNavTL.value.pause();

        document
          .querySelector(".fullscreen_navigation__content")!
          .scrollTo(0, 0);

        positionLightBehindKoempel();
        emit("toggleSidebar", "none");
        openNavTL.value.play(0);
      } else {
        openNavTL.value.pause();
        closeNavTL.value.play(0);
      }

      navStatus.value = !navStatus.value;
    };

    return { toggleSidebar, toggleFullscreenMenu };
  },
});
